/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import axiosInstance from "api/axiosInstance";
import { toast } from "react-toastify";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentMonth = months[new Date().getMonth()];

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
};

export default function usePayslipTableData(onPreview) {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loadingId, setLoadingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page,
        pageSize,
      });

      if (month) params.set("month", month);
      if (year) params.set("year", year);

      const res = await axiosInstance.get(`/hrportal/payslips?${params.toString()}`);

      if (res.data?.success) {
        setData(res.data.data || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (error) {
      toast.error("Failed to load payslips");
    }
  }, [month, page, pageSize, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const previewPayslip = async (id) => {
    try {
      setLoadingId(id);
      const res = await axiosInstance.post(`/hrportal/generate/${id}`);

      if (res.data?.success && onPreview) {
        onPreview(res.data.data);
        toast.success("Payslip details loaded");
      }
    } catch (error) {
      toast.error("Failed to load payslip details");
    } finally {
      setLoadingId(null);
    }
  };

  const rows = data.map((item) => ({
    Employee: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.employeeName}
      </MDTypography>
    ),
    Month: (
      <MDTypography variant="caption">
        {item.month} {item.year}
      </MDTypography>
    ),
    NetSalary: (
      <MDTypography variant="caption" fontWeight="medium">
        {formatCurrency(item.netSalary)}
      </MDTypography>
    ),
    Status: (
      <MDTypography variant="caption" color={item.isLocked ? "error" : "success"}>
        {item.isLocked ? "Locked" : "Ready"}
      </MDTypography>
    ),
    Action: (
      <MDBox display="flex" gap={1}>
        <MDButton
          size="small"
          variant="gradient"
          color="info"
          disabled={loadingId === item._id}
          onClick={() => previewPayslip(item._id)}
        >
          {loadingId === item._id ? "Loading..." : "Preview"}
        </MDButton>
      </MDBox>
    ),
  }));

  return {
    columns: [
      { Header: "Employee", accessor: "Employee", align: "left" },
      { Header: "Month", accessor: "Month", align: "center" },
      { Header: "Net Salary", accessor: "NetSalary", align: "center" },
      { Header: "Status", accessor: "Status", align: "center" },
      { Header: "Action", accessor: "Action", align: "center" },
    ],
    rows,
    totalCount,
    pagecount: pageSize,
    setPage,
    setMonth,
    setYear,
    year,
    month,
    refresh: fetchData,
  };
}
